const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export class ApiError<TBody = unknown> extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: TBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError<TBody>(error: unknown, status?: number): error is ApiError<TBody> {
  return error instanceof ApiError && (status === undefined || error.status === status);
}

// Get stored auth token
function getToken(): string | null {
  return localStorage.getItem('adminToken');
}

// Set auth token
export function setToken(token: string): void {
  localStorage.setItem('adminToken', token);
}

// Clear auth token
export function clearToken(): void {
  localStorage.removeItem('adminToken');
}

// Check if authenticated
export function isAuthenticated(): boolean {
  return !!getToken();
}

// API request helper
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(data.error?.message || 'Request failed', response.status, data);
  }

  return data;
}

// Blob/download request helper — mirrors apiRequest's auth header + 401 handling
async function apiBlobRequest(
  endpoint: string,
  options: RequestInit = {},
  errorMessage = 'Request failed'
): Promise<Blob> {
  const token = getToken();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    throw new Error(errorMessage);
  }

  return response.blob();
}

// Auth API
export const auth = {
  login: async (username: string, password: string) => {
    const data = await apiRequest<{ success: boolean; token: string }>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },
  logout: async () => {
    try {
      // Best-effort server logout; never block local logout on its outcome.
      await apiRequest('/admin/logout', { method: 'POST' });
    } catch {
      // Ignore server-side logout failures.
    } finally {
      clearToken();
    }
  },
};

// Keys API
export const keys = {
  list: () => apiRequest<{ keys: ApiKey[] }>('/admin/keys'),
  get: (id: string) => apiRequest<{ key: ApiKey }>(`/admin/keys/${id}`),
  create: (data: CreateKeyRequest) =>
    apiRequest<{ success: boolean; key: ApiKey & { apiKey: string } }>('/admin/keys', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateKeyRequest) =>
    apiRequest<{ success: boolean }>(`/admin/keys/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/keys/${id}`, {
      method: 'DELETE',
    }),
};

// Stats API
export const stats = {
  dashboard: (appKeyId?: string) =>
    apiRequest<{ stats: DashboardStats }>(
      `/admin/stats${appKeyId ? `?appKeyId=${appKeyId}` : ''}`
    ),
  usage: (days = 7, appKeyId?: string) =>
    apiRequest<{ data: UsageDataPoint[] }>(
      `/admin/stats/usage?days=${days}${appKeyId ? `&appKeyId=${appKeyId}` : ''}`
    ),
  models: (days = 7, appKeyId?: string) =>
    apiRequest<{ distribution: ModelDistribution[] }>(
      `/admin/stats/models?days=${days}${appKeyId ? `&appKeyId=${appKeyId}` : ''}`
    ),
  hourly: (days = 7, appKeyId?: string) =>
    apiRequest<{ heatmap: HourlyHeatmapData[] }>(
      `/admin/stats/hourly?days=${days}${appKeyId ? `&appKeyId=${appKeyId}` : ''}`
    ),
  compare: (days = 7) =>
    apiRequest<{ comparison: AppComparison[] }>(`/admin/stats/compare?days=${days}`),
};

// Logs API
export const logs = {
  requests: (filters: RequestFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.appKeyId) params.set('appKeyId', filters.appKeyId);
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{ requests: RequestLog[]; total: number }>(
      `/admin/requests?${params.toString()}`
    );
  },
  request: (id: string) => apiRequest<{ request: RequestLogDetail }>(`/admin/requests/${id}`),
  sessions: (filters: SessionFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.appKeyId) params.set('appKeyId', filters.appKeyId);
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{ sessions: Session[]; total: number }>(
      `/admin/sessions?${params.toString()}`
    );
  },
  sessionMessages: (sessionId: string) =>
    apiRequest<SessionMessages>(`/admin/sessions/${sessionId}/messages`),
  delete: (startDate: string, endDate: string) =>
    apiRequest<{ success: boolean; deletedCount: number }>('/admin/requests', {
      method: 'DELETE',
      body: JSON.stringify({ startDate, endDate }),
    }),
  conversations: (filters: ConversationFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.appKeyId) params.set('appKeyId', filters.appKeyId);
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{ conversations: ConversationSummary[]; total: number }>(
      `/admin/conversations?${params.toString()}`
    );
  },
  conversationMessages: (conversationId: string) =>
    apiRequest<ConversationMessages>(`/admin/conversations/${conversationId}/messages`),
  exportDataset: async (filters: DatasetExportFilters = {}): Promise<Blob> => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.appKeyId) params.set('appKeyId', filters.appKeyId);
    if (filters.minTurns) params.set('minTurns', filters.minTurns.toString());

    return apiBlobRequest(
      `/admin/export/dataset?${params.toString()}`,
      {},
      'Dataset export failed'
    );
  },
  purgeContent: (filters: PurgeFilters) =>
    apiRequest<{ success: boolean; rowsPurged: number }>('/admin/conversations/purge', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),
  export: async (format: 'json' | 'csv', filters: RequestFilters = {}): Promise<Blob> => {
    const params = new URLSearchParams();
    params.set('format', format);
    if (filters.appKeyId) params.set('appKeyId', filters.appKeyId);
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    return apiBlobRequest(
      `/admin/export/requests?${params.toString()}`,
      {},
      'Export failed'
    );
  },
};

// Settings API
export const settings = {
  get: () => apiRequest<{ settings: Settings }>('/admin/settings'),
  update: (data: Partial<Settings>) =>
    apiRequest<{ success: boolean }>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// Providers API
export const providersApi = {
  list: () => apiRequest<{ providers: Provider[] }>('/admin/providers'),
};

// Provider Keys API
export const providerKeys = {
  getStatus: () => apiRequest<{ status: ProviderKeyStatus }>('/admin/provider-keys/status'),
};

// Image Keys API
export const imageKeys = {
  list: () => apiRequest<{ keys: ImageApiKey[] }>('/admin/image-keys'),
  get: (id: string) => apiRequest<{ key: ImageApiKey }>(`/admin/image-keys/${id}`),
  create: (data: CreateImageKeyRequest) =>
    apiRequest<{ success: boolean; key: ImageApiKey & { apiKey: string } }>('/admin/image-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateImageKeyRequest) =>
    apiRequest<{ success: boolean }>(`/admin/image-keys/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/image-keys/${id}`, {
      method: 'DELETE',
    }),
};

// Image Backends API
export const imageBackends = {
  list: () => apiRequest<{ backends: ImageBackend[] }>('/admin/image-backends'),
  get: (id: string) => apiRequest<{ backend: ImageBackend }>(`/admin/image-backends/${id}`),
  create: (data: CreateImageBackendRequest) =>
    apiRequest<{ success: boolean; backend: ImageBackend }>('/admin/image-backends', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateImageBackendRequest) =>
    apiRequest<{ success: boolean }>(`/admin/image-backends/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/image-backends/${id}`, {
      method: 'DELETE',
    }),
};

// Wan video operational model control
export const wanVideoModelControl = {
  get: () =>
    apiRequest<WanVideoModelControlResponse>('/admin/wan-video-model-control'),
  update: (selection: WanVideoModelSelection, expectedRevision: number) =>
    apiRequest<WanVideoModelControlResponse>('/admin/wan-video-model-control', {
      method: 'PUT',
      body: JSON.stringify({ selection, expectedRevision }),
    }),
};

// Image Logs API
export const imageLogs = {
  requests: (filters: ImageRequestFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.imageKeyId) params.set('imageKeyId', filters.imageKeyId);
    if (filters.backendId) params.set('backendId', filters.backendId);
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{ requests: ImageRequestLog[]; total: number }>(
      `/admin/image-logs?${params.toString()}`
    );
  },
  request: (id: string) => apiRequest<{ request: ImageRequestLog }>(`/admin/image-logs/${id}`),
  stats: (imageKeyId?: string) =>
    apiRequest<{ stats: ImageStats }>(
      `/admin/image-logs/stats${imageKeyId ? `?imageKeyId=${imageKeyId}` : ''}`
    ),
};

// R2 Gallery API
export const r2Gallery = {
  list: (filters: R2GalleryFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.search) params.set('search', filters.search);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{ images: R2Image[]; total: number; limit: number; offset: number }>(
      `/admin/r2-images?${params.toString()}`
    );
  },
  stats: () => apiRequest<R2StorageStats>('/admin/r2-images/stats'),
  delete: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/admin/r2-images/${id}`, {
      method: 'DELETE',
    }),
  bulkDelete: (ids: string[]) =>
    apiRequest<{ success: boolean; deletedCount: number; requestedCount: number; errors: string[] }>(
      '/admin/r2-images/bulk-delete',
      {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }
    ),
};

// Uploads Gallery API (user-uploaded image attachments from image-captioning).
// Distinct from r2Gallery (which is generated images).
export const uploadsGallery = {
  list: (filters: UploadsGalleryFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.deviceId) params.set('deviceId', filters.deviceId);
    if (filters.tier) params.set('tier', filters.tier);
    if (filters.search) params.set('search', filters.search);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{
      uploads: UserUpload[];
      total: number;
      limit: number;
      offset: number;
    }>(`/admin/uploads?${params.toString()}`);
  },
  stats: () => apiRequest<UploadsStats>('/admin/uploads/stats'),
  get: (id: number) =>
    apiRequest<UserUpload>(`/admin/uploads/${id}`),
  delete: (id: number) =>
    apiRequest<{ deleted: boolean; id: number; r2_key: string }>(
      `/admin/uploads/${id}`,
      { method: 'DELETE' }
    ),
  bulkDelete: (ids: number[]) =>
    apiRequest<{
      success: boolean;
      r2Deleted: number;
      d1Deleted: number;
      requestedCount: number;
      errors: { id: number; error: string }[];
    }>('/admin/uploads/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
};

// Workflows API
export const workflows = {
  list: () => apiRequest<{ workflows: AdminWorkflow[] }>('/admin/workflows'),
  get: (id: number) => apiRequest<{ workflow: AdminWorkflow }>(`/admin/workflows/${id}`),
  create: (data: CreateWorkflowRequest) =>
    apiRequest<{ success: boolean }>('/admin/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: number, data: UpdateWorkflowRequest) =>
    apiRequest<{ success: boolean }>(`/admin/workflows/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: number) =>
    apiRequest<{ success: boolean }>(`/admin/workflows/${id}`, {
      method: 'DELETE',
    }),
  // Fire saved rules against sample input (server-side, real Worker code path)
  testReplacements: (id: number, body: { input: string; stage?: 'pre_transform' | 'post_transform' | 'both' }) =>
    apiRequest<TestReplacementsResponse>(`/admin/workflows/${id}/test-replacements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Fire unsaved rules against sample input (for live editor preview)
  testReplacementsPreview: (body: { rules: PromptReplacement[]; input: string; stage?: 'pre_transform' | 'post_transform' | 'both' }) =>
    apiRequest<TestReplacementsResponse>('/admin/workflows/test-replacements-preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// Transform Logs API
export const transformLogs = {
  list: (filters: TransformLogFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.workflowSlug) params.set('workflowSlug', filters.workflowSlug);
    if (filters.status) params.set('status', filters.status);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    return apiRequest<{ logs: TransformLog[]; total: number }>(
      `/admin/transform-logs?${params.toString()}`
    );
  },
  get: (id: string) => apiRequest<{ log: TransformLog }>(`/admin/transform-logs/${id}`),
  stats: () => apiRequest<{ stats: TransformStats }>('/admin/transform-logs/stats'),
};

// Types
export interface ApiKey {
  id: string;
  appName: string;
  keyPrefix: string;
  fullKey: string | null;
  createdAt: number;
  isActive: boolean;
  defaultProvider: string | null;
  forcedModel: string | null;
  requireAttestation: boolean;
  frequencyPenalty: number | null;
  presencePenalty: number | null;
  temperatureOverride: number | null;
  reasoningOverride: 0 | 1 | null;
  usage?: {
    tokensToday: number;
    costToday: number;
  };
}

export interface CreateKeyRequest {
  appName: string;
  defaultProvider?: string;
  forcedModel?: string;
  requireAttestation?: boolean;
  frequencyPenalty?: number | null;
  presencePenalty?: number | null;
  temperatureOverride?: number | null;
  reasoningOverride?: 0 | 1 | null;
}

export interface UpdateKeyRequest {
  appName?: string;
  isActive?: boolean;
  defaultProvider?: string | null;
  forcedModel?: string | null;
  requireAttestation?: boolean;
  frequencyPenalty?: number | null;
  presencePenalty?: number | null;
  temperatureOverride?: number | null;
  reasoningOverride?: 0 | 1 | null;
}

export interface DashboardStats {
  totalRequests24h: number;
  totalTokens24h: number;
  totalCost24h: number;
  activeApps: number;
  requestsTrend: number;
  tokensTrend: number;
  costTrend: number;
}

export interface UsageDataPoint {
  date: string;
  requests: number;
  tokens: number;
  cost: number;
}

export interface ModelDistribution {
  model: string;
  count: number;
  percentage: number;
}

export interface HourlyHeatmapData {
  hour: number;
  day: number;
  count: number;
}

export interface AppComparison {
  appName: string;
  appKeyId: string;
  requests: number;
  tokens: number;
  cost: number;
}

export interface RequestFilters {
  appKeyId?: string;
  deviceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface SessionFilters {
  appKeyId?: string;
  deviceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface RequestLog {
  id: string;
  appKeyId: string;
  deviceId: string | null;
  sessionId: string | null;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
  statusCode: number;
  responseTimeMs: number;
  isStreaming: boolean;
  createdAt: number;
  messageCount: number;
}

export interface RequestLogDetail extends Omit<RequestLog, 'messageCount'> {
  messages: ChatMessage[];
  responseContent: string | null;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface Session {
  id: string;
  appKeyId: string;
  deviceId: string;
  startedAt: number;
  lastActivity: number;
  requestCount: number;
}

export interface SessionMessages {
  sessionId: string;
  conversation: ConversationItem[];
  chatMessages: ChatMessageWithMeta[];
  deviceId: string;
  appKeyId: string;
  startedAt: number;
  lastActivity: number;
  requestCount: number;
}

export interface ConversationItem {
  id: string;
  timestamp: number;
  messages: ChatMessage[];
  response: string | null;
  model: string;
  provider: string;
}

export interface ChatMessageWithMeta {
  role: 'system' | 'user' | 'assistant';
  content: string;
  timestamp: number;
  requestId: string;
}

// Fallback chain item with provider and optional model
export interface FallbackItem {
  provider: string;
  model?: string;
}

// OpenRouter provider preferences (injected server-side into requests to OpenRouter)
export interface OpenRouterPreferences {
  sort?: 'price' | 'throughput' | 'latency';
  order?: string[];
  only?: string[];
  ignore?: string[];
  allow_fallbacks?: boolean;
  require_parameters?: boolean;
  data_collection?: 'allow' | 'deny';
  quantizations?: string[];
  max_price?: { prompt?: number; completion?: number };
  preferred_min_throughput?: number;
  preferred_max_latency?: number;
  transforms?: string[];
}

export interface Settings {
  default_provider: string;
  fallback_chain: FallbackItem[];
  enabled_models?: Record<string, string[]>;
  openrouter_preferences?: OpenRouterPreferences;
  log_retention_days: number;
  session_timeout_minutes: number;
  conversation_logging_enabled?: string; // 'true' or 'false'
  context_format_arm_override?: '' | 'today';
  // Image-captioning kill-switch — when 'true', the proxy saves attached
  // image bytes to R2 + writes a row to user_uploads. Default off.
  user_uploads_retention_enabled?: string; // 'true' or 'false'
}

export interface DatasetExportFilters {
  startDate?: string;
  endDate?: string;
  deviceId?: string;
  appKeyId?: string;
  minTurns?: number;
}

export interface PurgeFilters {
  startDate: string;
  endDate: string;
  deviceId?: string;
  appKeyId?: string;
}

export interface Provider {
  id: string;
  name: string;
  models: string[];
  supportsAllModels: boolean;
  enabledModels: string[];
}

export interface ProviderKeyStatus {
  openai: boolean;
  openrouter: boolean;
  nanogpt: boolean;
}

// Image Generation Types
export interface ImageApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  fullKey: string | null;
  createdAt: number;
  isActive: boolean;
  defaultBackend: string | null;
  requireAttestation: boolean;
  logLevel: 'none' | 'basic' | 'full';
  usage?: {
    requestsToday: number;
  };
}

export interface CreateImageKeyRequest {
  name: string;
  defaultBackend?: string;
  logLevel?: 'none' | 'basic' | 'full';
  requireAttestation?: boolean;
}

export interface UpdateImageKeyRequest {
  name?: string;
  isActive?: boolean;
  defaultBackend?: string | null;
  logLevel?: 'none' | 'basic' | 'full';
  requireAttestation?: boolean;
}

export type WanVideoModelSelection =
  | { mode: 'no_override' }
  | { mode: 'model'; modelId: string };

export interface WanVideoModelCatalogItem {
  id: string;
  displayName: string;
  costPerSecondUsd: number | null;
  costStatus: 'confirmed' | 'unconfirmed';
  notes: string[];
  supportedDurations: number[];
  supportedResolutions: string[];
  atlasFieldAcceptance: 'confirmed' | 'unconfirmed';
  dashScopeFallbackAvailable: boolean;
}

export interface WanVideoModelControlState {
  selection: WanVideoModelSelection;
  revision: number;
  updatedAt: number;
}

export interface WanVideoModelControlResponse {
  catalog: WanVideoModelCatalogItem[];
  state: WanVideoModelControlState;
}

export interface WanVideoModelControlConflictResponse extends WanVideoModelControlResponse {
  error: {
    message: string;
    code: 'revision_conflict';
    status: 409;
  };
}

export interface ImageBackend {
  id: string;
  name: string;
  type: 'runpod' | 'local' | 'atlas' | 'groq' | 'nanogpt' | 'gemini-edit';
  baseUrl: string;
  hasApiKey: boolean;
  priority: number;
  isActive: boolean;
  createdAt: number;
  timeout: number;
  maxConcurrent: number;
  /** Admin-configurable model name override. Currently only the
   *  gemini-edit backend reads this — other types ignore it. */
  defaultModel: string | null;
  stats: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    avgResponseTime: number;
  };
}

export interface CreateImageBackendRequest {
  name: string;
  type: 'runpod' | 'local' | 'atlas' | 'groq' | 'nanogpt' | 'gemini-edit';
  baseUrl: string;
  apiKey?: string;
  priority?: number;
  isActive?: boolean;
  timeout?: number;
  maxConcurrent?: number;
  defaultModel?: string;
}

export interface UpdateImageBackendRequest {
  name?: string;
  baseUrl?: string;
  apiKey?: string | null;
  priority?: number;
  isActive?: boolean;
  timeout?: number;
  maxConcurrent?: number;
  /** Pass empty string or null to clear; omit to leave unchanged. */
  defaultModel?: string | null;
}

export interface ImageRequestFilters {
  imageKeyId?: string;
  backendId?: string;
  deviceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface ImageRequestLog {
  id: string;
  imageKeyId: string;
  backendId: string | null;
  deviceId: string | null;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTimeMs: number;
  timestamp: number;
  promptId: string | null;
  workflowParams: string | null;
  errorMessage: string | null;
}

export interface ImageStats {
  totalRequests24h: number;
  successfulRequests24h: number;
  failedRequests24h: number;
  avgResponseTime24h: number;
  requestsTrend: number;
}

// R2 Gallery Types
export interface R2GalleryFilters {
  deviceId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface R2Image {
  id: string;
  prompt: string;
  deviceId: string | null;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  createdAt: string;
  r2Url: string;
  thumbnailUrl: string | null;
  format: string;
  mediaType: string; // 'image' | 'video'
}

export interface R2StorageStats {
  totalImages: number;
  totalBytes: number;
  totalMB: number;
}

// Uploads Gallery Types
export interface UploadsGalleryFilters {
  deviceId?: string;
  tier?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface UserUpload {
  id: number;
  r2_key: string;
  r2_url: string | null;
  device_id: string;
  tier: string | null;
  caption: string | null;
  user_text: string | null;
  mime_type: string | null;
  width: number | null;
  height: number | null;
  size_bytes: number | null;
  created_at: number;
  conversation_id: string | null;
  message_id: string | null;
}

export interface UploadsStats {
  totalUploads: number;
  totalBytes: number;
  totalMB: number;
  retentionEnabled: boolean;
}

// Devices API
export const devices = {
  list: (filters: DeviceFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    const qs = params.toString();
    return apiRequest<{ devices: Device[]; total: number }>(
      `/admin/devices${qs ? `?${qs}` : ''}`
    );
  },
  revoke: (deviceId: string) =>
    apiRequest<{ success: boolean }>(`/admin/devices/${deviceId}`, {
      method: 'DELETE',
    }),
};

// Device Types
export interface Device {
  deviceId: string;
  authMethod: string;
  environment: string;
  counter: number;
  createdAt: string;
  lastUsedAt: string;
}

export interface DeviceFilters {
  limit?: number;
  offset?: number;
}

// Workflow Types
export interface AdminWorkflow {
  id: number;
  slug: string;
  name: string;
  description: string;
  icon: string;
  workflowJson: string;
  providerParams: string | null;
  backendId: string;
  isPermanent: boolean;
  isEnabled: boolean;
  includeContext: boolean;
  promptTransformEnabled: boolean;
  promptTransformModel: string;
  promptTransformSystemPrompt: string;
  promptReplacements: string | null;   // JSON array of PromptReplacement (or null/empty = no rules)
  isConfigured: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowRequest {
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  workflow_json?: string;
  provider_params?: string;
  is_enabled?: boolean;
}

export interface UpdateWorkflowRequest {
  name?: string;
  slug?: string;
  description?: string;
  icon?: string;
  workflow_json?: string;
  provider_params?: string | null;
  is_enabled?: boolean;
  backend_id?: string;
  include_context?: boolean;
  prompt_transform_enabled?: boolean;
  prompt_transform_model?: string;
  prompt_transform_system_prompt?: string;
  prompt_replacements?: string | null;   // JSON-stringified PromptReplacement[]; null clears rules
}

// Prompt Replacements (regex find/replace rules applied to image prompts).
// Persisted as a JSON array in workflows.prompt_replacements. Fail-open at every
// layer — see worker/src/image/prompt-replacements.ts for execution semantics.
export interface PromptReplacement {
  id: string;
  name?: string;
  pattern: string;
  replacement: string;
  isRegex: boolean;
  caseInsensitive: boolean;
  multiline: boolean;
  dotAll: boolean;
  unicode: boolean;
  enabled: boolean;
  stage: 'pre_transform' | 'post_transform';
}

export interface RuleFireResult {
  ruleId: string;
  ruleName?: string;
  matchCount: number;
  durationMs: number;
  input: string;
  output: string;
  error?: string;
}

export interface StageTestResult {
  output: string;
  fires: RuleFireResult[];
  budgetExceeded: boolean;
  totalDurationMs: number;
  skippedInvalid: Array<{ ruleId: string; reason: string }>;
}

export type TestReplacementsResponse =
  | {
      ok: true;
      input: string;
      pre_transform: StageTestResult | null;
      post_transform: StageTestResult | null;
      final_output: string;
    }
  | {
      ok: false;
      validation_error: string;
    };

// Transform Log Types
export interface TransformLog {
  id: string;
  jobId: string;
  workflowId: number;
  workflowSlug: string;
  model: string;
  originalPrompt: string;
  transformedPrompt: string | null;
  status: 'success' | 'error' | 'timeout' | 'fallback';
  errorMessage: string | null;
  durationMs: number;
  inputTokens: number | null;
  outputTokens: number | null;
  createdAt: number;
}

export interface TransformLogFilters {
  workflowSlug?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface TransformStats {
  total: number;
  success_count: number;
  error_count: number;
  timeout_count: number;
  fallback_count: number;
  avg_duration_ms: number;
}

// Conversation Types
export interface ConversationFilters {
  appKeyId?: string;
  deviceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface ConversationSummary {
  conversationId: string;
  sessionId: string | null;
  appKeyId: string;
  deviceId: string | null;
  model: string;
  requestCount: number;
  startedAt: number;
  lastActivity: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
  isInferred: boolean;
  firstMessagePreview: string;
}

export interface ConversationMessages {
  conversationId: string;
  messages: DeduplicatedMessage[];
  model: string;
  appKeyId: string;
  deviceId: string | null;
  sessionId: string | null;
  startedAt: number;
  lastActivity: number;
  requestCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
  isInferred: boolean;
}

// A normalized content part. Present on DeduplicatedMessage.parts when the
// underlying chat message used the OpenAI vision content array (text +
// image_url). Lets the UI render images inline. `imageUrl` is camelCase
// (vs OpenAI wire format snake_case `image_url`) — matches what the worker
// emits in normalizeContent.
export interface MessagePart {
  type: 'text' | 'image_url';
  text?: string;
  imageUrl?: string;
}

export interface DeduplicatedMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
  parts?: MessagePart[];
  timestamp: number;
  requestId: string;
  model: string;
}

// Credits API
export const credits = {
  dashboard: () =>
    apiRequest<CreditsDashboard>('/admin/credits/dashboard'),
  users: (params: CreditUsersParams = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.sort) qs.set('sort', params.sort);
    if (params.order) qs.set('order', params.order);
    if (params.limit) qs.set('limit', params.limit.toString());
    if (params.offset) qs.set('offset', params.offset.toString());
    const q = qs.toString();
    return apiRequest<{ users: CreditUser[]; total: number }>(
      `/admin/credits/users${q ? `?${q}` : ''}`
    );
  },
  balance: (deviceId: string) =>
    apiRequest<CreditBalanceDetail>(`/admin/credits/balance/${deviceId}`),
  history: (deviceId: string, params: CreditHistoryParams = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', params.limit.toString());
    if (params.offset) qs.set('offset', params.offset.toString());
    if (params.type) qs.set('type', params.type);
    const q = qs.toString();
    return apiRequest<{ transactions: CreditTransaction[]; total: number }>(
      `/admin/credits/history/${deviceId}${q ? `?${q}` : ''}`
    );
  },
  grant: (body: CreditGrantRequest) =>
    apiRequest<{ success: boolean; new_balance: number; device_id: string }>('/admin/credits/grant', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  generations: (params: CreditGenerationsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.status) qs.set('status', params.status);
    if (params.device_id) qs.set('device_id', params.device_id);
    if (params.limit) qs.set('limit', params.limit.toString());
    if (params.offset) qs.set('offset', params.offset.toString());
    const q = qs.toString();
    return apiRequest<{ transactions: CreditTransaction[]; total: number; active_reservations: number }>(
      `/admin/credits/generations${q ? `?${q}` : ''}`
    );
  },
  costs: () =>
    apiRequest<{ costs: CreditCostEntry[] }>('/admin/credits/costs'),
  updateCost: (body: { gen_type: string; cost: number; ttl_seconds: number }) =>
    apiRequest<{ success: boolean }>('/admin/credits/costs', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  config: () =>
    apiRequest<{ config: CreditConfigEntry[] }>('/admin/credits/config'),
  updateConfig: (body: { key: string; value: string }) =>
    apiRequest<{ success: boolean }>('/admin/credits/config', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  purchases: (params: CreditPurchasesParams = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', params.limit.toString());
    if (params.offset) qs.set('offset', params.offset.toString());
    const q = qs.toString();
    return apiRequest<{ transactions: CreditTransaction[]; total: number }>(
      `/admin/credits/purchases${q ? `?${q}` : ''}`
    );
  },
  products: () =>
    apiRequest<{ products: CreditProductEntry[] }>('/admin/credits/products'),
  updateProduct: (body: { product_id: string; credits?: number; active?: boolean }) =>
    apiRequest<{ success: boolean }>('/admin/credits/products', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

// Credits Types
export interface CreditsDashboard {
  total_users: number;
  total_credits_granted: number;
  total_credits_used: number;
  total_credits_refunded: number;
  total_purchases: number;
  total_credits_purchased: number;
  total_subscriber_devices: number;
  active_reservations: number;
  today: {
    generations: number;
    images: number;
    videos: number;
    text_messages: number;
    failed: number;
    credits_consumed: number;
    credits_refunded: number;
    free_chat_messages: number;
    subscriber_messages: number;
    subscriber_devices: number;
    purchases: number;
    credits_purchased: number;
  };
}

export interface CreditUser {
  device_id: string;
  balance: number;
  total_granted: number;
  total_used: number;
  total_refunded: number;
  created_at: number;
  updated_at: number;
}

export interface CreditBalanceDetail {
  device_id: string;
  balance: number;
  total_granted: number;
  total_used: number;
  total_refunded: number;
  total_purchased: number;
  purchase_count: number;
  apple_user_id: string | null;
  is_attested: boolean;
  created_at: number;
  updated_at: number;
  pending_reservations: number;
}

export interface CreditTransaction {
  id: string;
  device_id: string;
  type: 'reserve' | 'debit' | 'refund' | 'grant' | 'purchase' | 'free_chat' | 'subscriber' | 'reserve_refunded' | 'search_free' | 'search_subscriber' | 'image_caption';
  amount: number;
  balance_after: number;
  gen_type: string | null;
  gen_job_id: string | null;
  reason: string | null;
  admin_note: string | null;
  purchase_transaction_id: string | null;
  purchase_environment: 'production' | 'sandbox' | null;
  created_at: number;
}

export interface CreditCostEntry {
  gen_type: string;
  cost: number;
  ttl_seconds: number;
  updated_at: number;
}

export interface CreditConfigEntry {
  key: string;
  value: string;
  updated_at: number;
}

export interface CreditUsersParams {
  search?: string;
  sort?: string;
  order?: string;
  limit?: number;
  offset?: number;
}

export interface CreditHistoryParams {
  limit?: number;
  offset?: number;
  type?: string;
}

export interface CreditGrantRequest {
  device_id: string;
  amount: number;
  reason: string;
  note?: string;
}

export interface CreditGenerationsParams {
  type?: string;
  status?: string;
  device_id?: string;
  limit?: number;
  offset?: number;
}

export interface CreditPurchasesParams {
  limit?: number;
  offset?: number;
}

export interface CreditProductEntry {
  product_id: string;
  credits: number;
  active: number;
  created_at: string;
}

// Calibration & Cost Analysis Types
export interface CalibrationData {
  startBalance: number;
  startTime: number;
  endBalance?: number;
  endTime?: number;
  totalGenerations?: number;
  costPerUnit?: number;
  status: 'in_progress' | 'completed';
}

export interface CalibrationResponse {
  image: CalibrationData | null;
  video: CalibrationData | null;
}

// Calibration & Cost Analysis API
export const calibration = {
  get: () =>
    apiRequest<{ calibration: CalibrationResponse }>('/admin/calibration'),
  start: (type: 'image' | 'video', balance: number) =>
    apiRequest<{ success: boolean; calibration: CalibrationData }>('/admin/calibration/start', {
      method: 'POST',
      body: JSON.stringify({ type, balance }),
    }),
  end: (type: 'image' | 'video', balance: number) =>
    apiRequest<{ success: boolean; calibration: CalibrationData; summary: { totalSpend: string; totalGenerations: number; costPerUnit: string } }>(
      '/admin/calibration/end',
      {
        method: 'POST',
        body: JSON.stringify({ type, balance }),
      }
    ),
  remove: (type: 'image' | 'video') =>
    apiRequest<{ success: boolean }>(`/admin/calibration?type=${type}`, {
      method: 'DELETE',
    }),
};

// Blocklist API
export const blocklist = {
  list: (scope?: string, category?: string) => {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (category) params.set('category', category);
    const qs = params.toString();
    return apiRequest<{ entries: BlocklistEntry[] }>(
      `/admin/blocklist${qs ? `?${qs}` : ''}`
    );
  },
  create: (data: CreateBlocklistEntry) =>
    apiRequest<{ success: boolean; entry: BlocklistEntry }>('/admin/blocklist', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/blocklist/${id}`, {
      method: 'DELETE',
    }),
  test: (text: string, scope: 'text' | 'image') =>
    apiRequest<{ violations: BlocklistViolation[]; matchCount: number }>(
      '/admin/blocklist/test',
      {
        method: 'POST',
        body: JSON.stringify({ text, scope }),
      }
    ),
};

// Blocklist Types
export interface BlocklistEntry {
  id: string;
  scope: 'text' | 'image' | 'both';
  type: 'word' | 'phrase' | 'pattern';
  category: 'csam' | 'real_person' | 'self_harm' | 'sexual' | 'terrorism' | 'other';
  value: string;
  created_at: string;
}

export interface CreateBlocklistEntry {
  value: string;
  type: 'word' | 'phrase' | 'pattern';
  scope: 'text' | 'image' | 'both';
  category: 'csam' | 'real_person' | 'self_harm' | 'sexual' | 'terrorism' | 'other';
}

export interface BlocklistViolation {
  type: string;
  category: string;
  value: string;
  matchedIn: string;
}

// Feedback Types
export interface FeedbackItem {
  id: string;
  device_id: string | null;
  category: 'bug' | 'suggestion' | 'other';
  description: string;
  app_version: string | null;
  ios_version: string | null;
  device_model: string | null;
  email: string | null;
  has_screenshot: boolean;
  status: 'new' | 'reviewed' | 'in_progress' | 'done' | 'wont_fix';
  admin_notes: string | null;
  admin_tags: string | null;
  created_at: string;
  updated_at: string;
}

/** Full detail response (includes screenshot blob) */
export interface FeedbackDetail extends Omit<FeedbackItem, 'has_screenshot'> {
  screenshot_base64: string | null;
}

export interface FeedbackFilters {
  category?: string;
  status?: string;
  search?: string;
  tag?: string;
  startDate?: string;
  endDate?: string;
  sort?: 'newest' | 'oldest';
  limit?: number;
  offset?: number;
}

export interface FeedbackStats {
  total: number;
  today: number;
  by_status: Record<string, number>;
  by_category: Record<string, number>;
  tags: string[];
}

export interface FeedbackBulkRequest {
  ids: string[];
  action: 'update_status' | 'delete' | 'add_tag';
  status?: string;
  tag?: string;
}

// Diagnostics types (MetricKit payloads)
export interface DiagnosticEntry {
  id: string;
  device_id: string | null;
  payload_type: string;
  app_version: string | null;
  ios_version: string | null;
  device_model: string | null;
  payload_json?: string;
  created_at: string;
  diagnostic_kinds?: Array<'crash' | 'hang' | 'disk_write' | 'cpu_exception'>;
}

export interface DiagnosticsFilters {
  payload_type?: string;
  device_id?: string;
  startDate?: string;
  endDate?: string;
  sort?: string;
  limit?: number;
  offset?: number;
  diagnostic_kind?: 'crash_or_hang';
}

export interface DiagnosticsStats {
  total: number;
  today: number;
  by_type: Record<string, number>;
  crashes: number;
  hangs: number;
  unique_devices: number;
  sample_limit?: number;
  sampled_diagnostic_reports?: number;
}

export interface AppHealthHistogramBucket {
  start_ms: number;
  end_ms: number | null;
  count: number;
}

export interface AppHealthData {
  sample_limit: number;
  sampled: {
    diagnostic_reports: number;
    metric_reports: number;
  };
  totals: {
    reports: number;
    devices: number;
    metrics: number;
    diagnostics: number;
  };
  by_app_version: Array<{
    app_version: string;
    rows: number;
    devices: number;
  }>;
  diagnostic_counts: {
    crashes: number;
    hangs: number;
    disk_writes: number;
    cpu_exceptions: number;
  };
  worst_hang_ms: number | null;
  hang_histogram: AppHealthHistogramBucket[];
  launch_histogram: AppHealthHistogramBucket[];
  peak_memory: {
    reports: number;
    average_mb: number | null;
    median_mb: number | null;
    max_mb: number | null;
  };
  trend: Array<{
    week_start: string;
    reports: number;
    devices: number;
    metrics: number;
    diagnostics: number;
  }>;
  trend_weeks: number;
}

// Diagnostics API
export const diagnosticsApi = {
  list: (filters: DiagnosticsFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.payload_type) params.set('payload_type', filters.payload_type);
    if (filters.device_id) params.set('device_id', filters.device_id);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    if (filters.diagnostic_kind) params.set('diagnostic_kind', filters.diagnostic_kind);
    const qs = params.toString();
    return apiRequest<{ diagnostics: DiagnosticEntry[]; total: number; limit: number; offset: number }>(
      `/admin/diagnostics${qs ? `?${qs}` : ''}`
    );
  },
  stats: () => apiRequest<DiagnosticsStats>('/admin/diagnostics/stats'),
  health: () => apiRequest<AppHealthData>('/admin/diagnostics/health'),
  get: (id: string) =>
    apiRequest<{ diagnostic: DiagnosticEntry }>(`/admin/diagnostics/${id}`),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/diagnostics/${id}`, {
      method: 'DELETE',
    }),
  bulk: (data: { ids: string[]; action: 'delete' }) =>
    apiRequest<{ success: boolean; affected: number }>('/admin/diagnostics/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Feedback API
export const feedback = {
  list: (filters: FeedbackFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    if (filters.tag) params.set('tag', filters.tag);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());
    const qs = params.toString();
    return apiRequest<{ feedback: FeedbackItem[]; total: number; limit: number; offset: number }>(
      `/admin/feedback${qs ? `?${qs}` : ''}`
    );
  },
  stats: () => apiRequest<FeedbackStats>('/admin/feedback/stats'),
  get: (id: string) => apiRequest<FeedbackDetail>(`/admin/feedback/${id}`),
  update: (id: string, data: { status?: string; admin_notes?: string; admin_tags?: string }) =>
    apiRequest<{ success: boolean }>(`/admin/feedback/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/feedback/${id}`, {
      method: 'DELETE',
    }),
  bulk: (data: FeedbackBulkRequest) =>
    apiRequest<{ success: boolean; affected: number }>('/admin/feedback/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ============================================================================
// Web Search API (Tavily + future providers)
// ============================================================================

export interface SearchBackend {
  id: string;
  name: string;
  type: 'tavily';
  baseUrl: string;
  hasApiKey: boolean;
  isActive: boolean;
  priority: number;
  searchDepth: 'basic' | 'advanced';
  maxResultsDefault: number;
  costPerQueryUsd: number | null;
  timeoutMs: number;
  maxConcurrent: number;
  createdAt: number;
  updatedAt: number;
  stats?: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    avgResponseTime: number;
  };
}

export interface CreateSearchBackendRequest {
  name: string;
  type: 'tavily';
  baseUrl: string;
  apiKey?: string;
  priority?: number;
  isActive?: boolean;
  searchDepth?: 'basic' | 'advanced';
  maxResultsDefault?: number;
  costPerQueryUsd?: number | null;
  timeoutMs?: number;
  maxConcurrent?: number;
}

export interface UpdateSearchBackendRequest {
  name?: string;
  baseUrl?: string;
  apiKey?: string | null;       // null or '' clears; undefined preserves
  priority?: number;
  isActive?: boolean;
  searchDepth?: 'basic' | 'advanced';
  maxResultsDefault?: number;
  costPerQueryUsd?: number | null;
  timeoutMs?: number;
  maxConcurrent?: number;
}

export interface SearchLogEntry {
  id: string;
  device_id: string | null;
  backend_id: string | null;
  query: string;
  result_count: number | null;
  cost_credits: number | null;
  cost_usd: number | null;
  status_code: number | null;
  response_time_ms: number | null;
  request_type: string | null;
  error: string | null;
  created_at: number;
}

export interface SearchLogFilters {
  deviceId?: string;
  backendId?: string;
  statusCode?: number;
  limit?: number;
  offset?: number;
}

export const searchBackends = {
  list: () => apiRequest<{ backends: SearchBackend[] }>('/admin/search-backends'),
  get: (id: string) => apiRequest<{ backend: SearchBackend }>(`/admin/search-backends/${id}`),
  create: (data: CreateSearchBackendRequest) =>
    apiRequest<{ success: boolean; backend: SearchBackend }>('/admin/search-backends', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateSearchBackendRequest) =>
    apiRequest<{ success: boolean }>(`/admin/search-backends/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/search-backends/${id}`, {
      method: 'DELETE',
    }),
};

export const searchLogs = {
  list: (filters: SearchLogFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.deviceId)   params.set('device_id',  filters.deviceId);
    if (filters.backendId)  params.set('backend_id', filters.backendId);
    if (filters.statusCode !== undefined) params.set('status_code', String(filters.statusCode));
    if (filters.limit  !== undefined) params.set('limit',  String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return apiRequest<{ logs: SearchLogEntry[]; total: number; limit: number; offset: number }>(
      `/admin/search-logs${qs ? `?${qs}` : ''}`
    );
  },
  get: (id: string) => apiRequest<{ log: SearchLogEntry }>(`/admin/search-logs/${id}`),
};
